import type React from 'react';

import { Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { useI18n } from '@change-corgi/core/react/i18n';

type Theme = {
	dataTestid: string;
	statusText: string;
	wrapperClass: string;
	textClass: string;
};

export const NotifiedOrRespondedStatus: React.FC<{
	daysSinceNotified: number | null | undefined;
	isNotified: boolean;
	hasResponded: boolean;
}> = ({ daysSinceNotified, isNotified, hasResponded }) => {
	const { translate } = useI18n();
	const awaitingResponse = !isNotified && !hasResponded;

	const getNotifiedSinceText = () => {
		if (awaitingResponse)
			return translate('corgi.petition.starter_dashboard_v2.decision_makers.cards.status.awaiting_response');
		if (daysSinceNotified === null || daysSinceNotified === undefined)
			return translate('corgi.petition.starter_dashboard_v2.decision_makers.cards.status.not_responded');
		if (daysSinceNotified === 0)
			return translate('corgi.petition.starter_dashboard_v2.decision_makers.cards.status.no_response_notified_today');
		if (daysSinceNotified === 1)
			return translate('corgi.petition.starter_dashboard_v2.decision_makers.cards.status.no_response_notified_one_day');
		return translate('corgi.petition.starter_dashboard_v2.decision_makers.cards.status.no_response_notified_days_ago', {
			days: daysSinceNotified,
		});
	};

	let theme: Theme;

	if (hasResponded) {
		theme = {
			dataTestid: 'responded-status',
			statusText: translate('corgi.petition.starter_dashboard_v2.decision_makers.cards.status.responded'),
			wrapperClass: 'bg-[#EEFFF5] border-[#0DDA62]',
			textClass: 'text-typography-success',
		};
	} else if (awaitingResponse) {
		theme = {
			dataTestid: 'awaiting-status',
			statusText: getNotifiedSinceText(),
			wrapperClass: 'bg-white border-[#918A9D]',
			textClass: 'text-typography-strong',
		};
	} else {
		theme = {
			dataTestid: 'notified-status',
			statusText: getNotifiedSinceText(),
			wrapperClass: 'bg-[#FEF1F3] border-[#FF9DAD]',
			textClass: 'text-typography-brand',
		};
	}

	const baseWrapper = 'inline-flex items-center rounded-full border px-2 py-0.5';

	return (
		<div data-testid={theme.dataTestid} data-qa={theme.dataTestid}>
			<div className={cn(baseWrapper, theme.wrapperClass)}>
				<Text size="small" variant="body" fontWeight="bold" className={theme.textClass}>
					{theme.statusText}
				</Text>
			</div>
		</div>
	);
};
